/* eslint-disable no-console */
import constants from "../constants";
import { getFeatureFlagValueLegacy } from "../services/featureFlags";
import { FeatureFlagName } from "./featureFlagName";
import { getServerDate } from "./storage/date";
import  { LogType, Source, Event, ILog, AdditionalDetails } from "./logger";
import { msToDurationString } from "./date";
import { IApiError } from "./api.interfaces";
import errorFilter from "./error.filter";
import { isNetworkError } from "./errorHandler/error.types";
import { cleanSessionAndPassword } from "./helpers";
import { DataActions } from "../store/data/actions";
import { addLogToDb, getAllLogsAndKeysFromDb } from "./indexedDbLogger";

let skipConsoleLog: string[] = [Source[Source.ROUTER], Source[Source.USER_EVENT]];
let timers: {[timerId: string]: Date} = {};
let pendingLogInterval: number | undefined;
let pendingLogs: ILog[] = [];
let instanceId: string | undefined;
let sequence = 0;

/** Cache for parsed targeted-logging patterns to avoid re-parsing on every log call. */
let parsedPatternsCache: { flagValue: string; patterns: [string, string, string][]; hasFullWildcard: boolean } | undefined = undefined;

/** @internal Exposed for testing only. Resets the parsed-patterns cache. */
export function resetParsedPatternsCache() {
    parsedPatternsCache = undefined;
}

/** Cache mapping lowercased username → resolved Geotab userId. */
let userIdCache: Map<string, string> = new Map();

/** @internal Exposed for testing only. Resets the username → userId cache. */
export function resetUserIdCache() {
    userIdCache = new Map();
}

/** Returns the next sequence number, incrementing the shared counter used by all log entries. */
export function getNextSequence(): number {
    return ++sequence;
}

export type UserArg = string | { name: string } | { id: string } | null | undefined;

/**
 * Resolves a `userName` string from the `user` argument passed to log functions.
 *
 * - `string` → lowercased
 * - `{ name }` → lowercased name
 * - `{ id }` → `"id:<id>"`
 * - `null` / `undefined` / unrecognised shape → `undefined`
 */
export function resolveUserName(user: UserArg): string | undefined {
    if (typeof user === "string") {
        return user.toLowerCase();
    }
    if (user && typeof user === "object") {
        if ("name" in user) {
            return user.name.toLowerCase();
        }
        if ("id" in user) {
            return `id:${ user.id }`;
        }
    }
    return undefined;
}

/**
 * Resolves a comma-delimited string of lowercased usernames (as produced by `resolveUserName`)
 * into an array of Geotab user IDs, using `userService.getUserByName()` with per-username caching.
 *
 * Usernames of the form `id:<id>` (produced when only an `id` was available) are passed through
 * directly as `<id>` without a store lookup.
 *
 * Usernames that cannot be resolved (user not found in the store) are omitted from the result.
 *
 * @param userName - A single lowercased username or a comma-delimited list (e.g. `"alice,bob"`)
 * @returns An array of resolved Geotab user IDs. Empty if none could be resolved.
 */
export function resolveUserIds(userName: string): string[] {
    const names = userName.split(",").map(n => n.trim()).filter(Boolean);
    const ids: string[] = [];

    for (const name of names) {
        // Fast path: already in cache
        const cached = userIdCache.get(name);
        if (cached !== undefined) {
            ids.push(cached);
            continue;
        }

        // id:<id> shorthand — the caller only had a numeric/string id, not a username
        if (name.startsWith("id:")) {
            const id = name.slice(3);
            userIdCache.set(name, id);
            ids.push(id);
            continue;
        }

        // Look up via userService
        const id = window?.driveApp?.userService?.getUserByName(name)?.id;
        if (id) {
            userIdCache.set(name, id);
            ids.push(id);
        }
    }

    return ids;
}

/**
 * Parses the raw flag value string into a list of valid 3-part patterns and a full-wildcard flag.
 * Results are cached by flag value to avoid re-parsing on every log call.
 */
function getParsedPatterns(flagValue: string): { patterns: [string, string, string][]; hasFullWildcard: boolean } {
    if (parsedPatternsCache && parsedPatternsCache.flagValue === flagValue) {
        return parsedPatternsCache;
    }

    const rawPatterns = flagValue.split(",").map((p: string) => p.trim()).filter(Boolean);
    const patterns: [string, string, string][] = [];

    for (const pattern of rawPatterns) {
        const parts = pattern.split("|");

        // Pattern must have exactly 3 parts: [TYPE, SOURCE, USERID]
        if (parts.length !== 3) {
            continue;
        }

        patterns.push(parts as [string, string, string]);
    }

    const hasFullWildcard = patterns.some(([t, s, u]) => t === "%" && s === "%" && u === "%");

    parsedPatternsCache = { flagValue, patterns, hasFullWildcard };
    return parsedPatternsCache;
}

/**
 * Returns `true` when the `DRIVE.DEBUG_LOGGING` flag contains the full-wildcard pattern `%|%|%`,
 * which enables verbose logging for all sources, types, and users.
 * Also returns `true` when `constants.LOGGING.VERBOSE_OVERRIDE` is set at runtime.
 */
export function isVerboseLogging(): boolean {
    if (constants.LOGGING.VERBOSE_OVERRIDE) {
        return true;
    }
    const flagValue = getFeatureFlagValueLegacy(FeatureFlagName.DEBUG_LOGGING, "");
    if (!flagValue) {
        return false;
    }
    const { hasFullWildcard } = getParsedPatterns(flagValue);
    return hasFullWildcard;
}

/**
 * Returns `true` if the `DRIVE.DEBUG_LOGGING` feature flag has a pattern matching the given
 * source, type, and user. Patterns are `TYPE|SOURCE|USERID` tuples; each component can be a
 * specific value or `%` (wildcard). `%|%|%` matches everything.
 *
 * `userName` is resolved to a Geotab user ID via `userService.getUserByName()`. Pre-login
 * (no user resolved), only patterns with a `%` user dimension match.
 */
export function matchesTargetedLogging(source: string, type: string, userName?: string): boolean {
    const flagValue = getFeatureFlagValueLegacy(FeatureFlagName.DEBUG_LOGGING, "");
    if (!flagValue) {
        return false;
    }
    const { patterns, hasFullWildcard } = getParsedPatterns(flagValue);
    if (patterns.length === 0) {
        return false;
    }
    if (hasFullWildcard) {
        return true;
    }

    // Resolve username(s) to Geotab user IDs.
    // Pre-login (no user resolved), only patterns with "%" in the user dimension match.
    const resolvedUserName = userName || window?.driveApp?.userService?.getCurrentUser()?.name;
    const noUserResolved = !resolvedUserName;
    const targetUserIds = noUserResolved ? [] : resolveUserIds(resolvedUserName);
    if (!noUserResolved && targetUserIds.length === 0) {
        return false;
    }

    for (const [patternType, patternSource, patternUserId] of patterns) {
        const typeMatches = patternType === "%" || type === patternType;
        const sourceMatches = patternSource === "%" || source === patternSource;
        const userIdMatches = patternUserId === "%" || (!noUserResolved && targetUserIds.indexOf(patternUserId) !== -1);

        if (typeMatches && sourceMatches && userIdMatches) {
            return true;
        }
    }

    return false;
}

/**
 * Returns `true` if the `DRIVE.DEBUG_LOGGING` feature flag has a pattern that matches the given
 * source and type. Use this to gate extra diagnostic code blocks that should only run when
 * targeted logging is active.
 *
 * Uses strict dimension matching — each specified dimension must match exactly in the pattern
 * (not via a pattern-side wildcard). `%|%|%` always matches unless `skipFullWildcard` is `true`.
 *
 * **User dimension:** Patterns with a `%` user field always match. Patterns with a specific user
 * ID are matched against the `user` argument when provided; when omitted, falls back to the
 * currently logged-in user (via `window.driveApp.userService.getCurrentUser()`). Pre-login or
 * when no user can be resolved, user-specific patterns do not match. This allows verbose extra
 * logging (such as noisy GPS-tick data) to be targeted to an individual driver without enabling
 * it database-wide. Pass the log's attributed user (e.g. the GPS-callback user in movement.ts)
 * to ensure the gate and the log attribution agree in multi-driver sessions.
 *
 * @param source - Optional source to match against. When provided, the pattern source dimension
 *   must equal this value exactly (pattern-side `%` does not satisfy it).
 * @param type - Optional log type to match against. When provided, the pattern type dimension
 *   must equal this value exactly (pattern-side `%` does not satisfy it).
 * @param skipFullWildcard - When `true`, the full-wildcard pattern is skipped during matching.
 *   All other patterns are still evaluated normally, so a flag containing both the full wildcard
 *   and a specific pattern will still return `true` if the specific pattern matches. Use this
 *   when you need to know whether logging is *specifically* targeted at this source/type, not
 *   just "log everything". Defaults to `false`.
 * @param user - The user to compare against user-specific patterns. When provided, this value
 *   is used directly and `getCurrentUser()` is not called. Pass the same user object you pass
 *   to `logger.log()` so the gate and the log attribution always agree (important in multi-driver
 *   sessions where the GPS-callback user may differ from the active UI user). When omitted, falls
 *   back to `getCurrentUser()` — appropriate for call sites with no per-call user context.
 *
 * | Call | Matches |
 * |---------|---------|
 * | `shouldAddExtraLogging(Source.IAM)` | `%\|IAM\|%`, `%\|%\|%`, `%\|IAM\|<currentUserId>` (when logged in) |
 * | `shouldAddExtraLogging(undefined, LogType.ERROR)` | `ERROR\|%\|%`, `%\|%\|%` |
 * | `shouldAddExtraLogging(Source.IAM, LogType.ERROR)` | `ERROR\|IAM\|%`, `%\|%\|%` |
 * | `shouldAddExtraLogging(Source.GEO, undefined, true)` | `%\|GEO\|%`, `%\|GEO\|<currentUserId>` (full wildcard skipped) |
 * | `shouldAddExtraLogging(Source.GEO, undefined, true, user)` | `%\|GEO\|<user.id>` (full wildcard skipped; explicit user) |
 *
 * @example
 * ```typescript
 * if (shouldAddExtraLogging(Source.IAM)) { ... }
 * if (shouldAddExtraLogging(Source.IAM, LogType.ERROR)) { ... }
 * // Pass the attributed user so the gate agrees with the log in multi-driver sessions.
 * if (shouldAddExtraLogging(Source.GEO, undefined, true, user)) { ... }
 * ```
 */
export function shouldAddExtraLogging(source?: Source, type?: LogType, skipFullWildcard = false, user?: UserArg): boolean {
    const flagValue = getFeatureFlagValueLegacy(FeatureFlagName.DEBUG_LOGGING, "");
    if (!flagValue) {
        return false;
    }
    const { patterns, hasFullWildcard } = getParsedPatterns(flagValue);
    if (patterns.length === 0) {
        return false;
    }
    if (hasFullWildcard && !skipFullWildcard) {
        return true;
    }

    const sourceStr = source !== undefined ? Source[source] : undefined;
    const typeStr = type !== undefined ? LogType[type] : undefined;

    // Lazy, memoized user resolution — only computed the first time a non-wildcard user
    // pattern is reached. Uses the caller-supplied user first; falls back to getCurrentUser()
    // for call sites with no per-call user context (e.g. IAM, SESSION).
    // On the %|GEO|% (wildcard) path the closure never executes, eliminating the per-tick
    // deep clone of the users store that was present when resolution was eager.
    let resolved = false;
    let cachedUserIds: string[] = [];
    const getUserIds = (): string[] => {
        if (!resolved) {
            resolved = true;
            const effectiveUser = user ?? window?.driveApp?.userService?.getCurrentUser();
            const name = resolveUserName(effectiveUser);
            cachedUserIds = name ? resolveUserIds(name) : [];
        }
        return cachedUserIds;
    };

    for (const [patternType, patternSource, patternUserId] of patterns) {
        // Skip %|%|% when skipFullWildcard is set — it was already handled (or intentionally bypassed) above
        if (skipFullWildcard && patternType === "%" && patternSource === "%" && patternUserId === "%") {
            continue;
        }

        // When caller specifies a value, pattern must match exactly.
        // When caller omits a value (undefined), pattern must have "%".
        const typeMatches = typeStr === undefined ? patternType === "%" : patternType === typeStr;
        const sourceMatches = sourceStr === undefined ? patternSource === "%" : patternSource === sourceStr;
        if (!typeMatches || !sourceMatches) {
            continue;
        }

        // User wildcard — always matches; no user resolution needed
        if (patternUserId === "%") {
            return true;
        }

        // User-specific pattern: compare against the caller-supplied user (or current user as
        // fallback). Pre-login (getUserIds returns []), these patterns do not match.
        if (getUserIds().indexOf(patternUserId) !== -1) {
            return true;
        }
    }

    return false;
}

function isActiveInstance(): boolean {
    if (window && window.driveApp && window.driveApp.store) {
        return instanceId === window?.driveApp?.store?.getState().data.instanceId;
    }
    // still initializing, return true for now
    return true;
}

export function getPendingLogs() {
    return pendingLogs;
}


export function getPendingLogInterval() {
    return pendingLogInterval;
}

function padString(inputString: string, length = 0, enforceMaxLength = false) {
    if (inputString.length > length) {
        return enforceMaxLength ? inputString.substr(0, length) : inputString;
    }
    return inputString + new Array(length - inputString.length + 1).join(" ");
}

function logToConsole(type: LogType, source: string, userName: string, date: string, messageArray: (string | null)[]) {
    const enableLogging = window.DRIVE_DEBUG?.ENABLE_LOGGING;
    if (enableLogging !== true) {
        const shouldConsoleLog = matchesTargetedLogging(source, LogType[type], userName);
        if (!shouldConsoleLog && (skipConsoleLog.indexOf(source) !== -1 || window.DRIVE_DEBUG)) {
            return;
        }
    }
    let consoleLogMessage: (string | null)[];
    consoleLogMessage = messageArray.slice(0);
    if (userName) {
        consoleLogMessage.unshift(`(${ userName })`);
    }
    consoleLogMessage.unshift(padString(source, 10));
    consoleLogMessage.unshift(date);
    switch (type) {
        case LogType.FEEDBACK:
            break;
        case LogType.ERROR:
            if (window.device) {
                console.error(consoleLogMessage.join(" | "));
            } else {
                try {
                    console.error.apply(console, consoleLogMessage);
                } catch (e) {
                    console.error(consoleLogMessage.join("\t"));
                }
            }
            break;
        default:
            if (window.device) {
                console.log(consoleLogMessage.join(" | "));
            } else {
                try {
                    console.log.apply(console, consoleLogMessage);
                } catch (e) {
                    console.log(consoleLogMessage.join("\t"));
                }
            }
            break;
    }
}

// Trims logs array to match the log limit
export function trimLogs(logs: ILog[], logLimit: number) {
    if (logs.length > logLimit) {
        logs.splice(0, logs.length - logLimit);
    }
}

export function uploadToBigQueryPredicate (l: ILog): boolean {
    switch (l.source) {
        case Source[Source.USER_EVENT]:
        case Source[Source.NATIVE]:
        case Source[Source.NATIVE_SDK_INFO]:
        case Source[Source.NATIVE_SDK_ERROR]:
        case Source[Source.UNTRANSLATED_STRING]:
        case Source[Source.ADDIN_STARTUP]:
        case Source[Source.SERVWORKER]:
        case Source[Source.LAUNCHER]:
        case Source[Source.INDEXED_DB]:
        case Source[Source.STORAGE]:
        case Source[Source.ADDIN_STORAGE_AUDIT]:
        case Source[Source.IAM]:
        case Source[Source.WEBSOCKET_ACK]:
            return true;
        default:
            break;
    }

    switch (l.type) {
        case LogType[LogType.EVENT]:
        case LogType[LogType.ERROR]:
        case LogType[LogType.FEEDBACK]:
        case LogType[LogType.SURVEY]:
            return true;
        default:
            break;
    }

    if (isVerboseLogging()) {
        return true;
    }

    if (matchesTargetedLogging(l.source, l.type, l.user)) {
        return true;
    }

    return false;
}

export function sanitizeMessage(message: string | string[]) {
    let messageArray = ([] as string[]).concat.call([], message).map((msg: string): string | null => {
        return msg ? cleanSessionAndPassword(msg) : null;
    });
    return messageArray;
}

async function pushLogs(pendingLog: ILog) {
    await addLogToDb(pendingLog);
}

async function createLog(
    type: LogType,
    source: string,
    message: string | string[],
    user: UserArg,
    allUsers: DeepReadonly<IIdDictionary<IUser>>,
    duration?: number | any,
    connection?: string | any,
    shouldLimitSize = true,
    additionalDetails?: AdditionalDetails,
    errorObject?: IApiError
): Promise<void> {
    let date = getServerDate().toISOString();
    let messageArray = sanitizeMessage(message);

    if (Object.prototype.toString.call(messageArray) !== "[object Array]") {
        throw new Error(`Invalid error message: ${ JSON.stringify(messageArray) }`);
    }

    const userName = resolveUserName(user) ?? Object.keys(allUsers).join(",");

    logToConsole(type, source, userName, date, messageArray);

    if (errorObject && (errorFilter.isIgnored(errorObject) || isNetworkError(errorObject))) {
        // Do not create a log if the error is to be ignored, log to console only
        return;
    }

    let stringifiedMessage = messageArray.join(", ");

    if (shouldLimitSize && stringifiedMessage.length && stringifiedMessage.length > constants.LOGGING.LOG_SIZE_LIMIT) {
        stringifiedMessage = `{TRUNC} ${ stringifiedMessage.substring(0, constants.LOGGING.LOG_SIZE_LIMIT) }`;
    }

    let pendingLog: ILog = {
        type: LogType[type],
        date,
        source,
        message: stringifiedMessage,
        user: userName,
        instanceId: `${ instanceId }-${ isActiveInstance() ? "1" : "0" }`,
        sequence: getNextSequence()
    };

    if (window.device && window.device.uuid) {
        pendingLog.deviceUuid = window.device.uuid;
    }
    if (duration) {
        pendingLog.duration = duration;
    }
    if (connection) {
        pendingLog.connectionType = connection;
    }
    if (additionalDetails) {
        pendingLog.additionalDetails = additionalDetails;
    }

    await pushLogs(pendingLog);
}

export function log(from: Source, message: string | string[], allUsers: DeepReadonly<IIdDictionary<IUser>>, user?: string | any): void {
    createLog(LogType.LOG, Source[from], message, user, allUsers);
}

export function error(from: Source, message: string | string[], allUsers: DeepReadonly<IIdDictionary<IUser>>, user?: string | any, shouldLimitSize = true, errorObject?: IApiError): void {
    createLog(LogType.ERROR, Source[from], message, user, allUsers, null, null, shouldLimitSize, undefined, errorObject);
}

export function event(
    from: Event,
    message: string | string[],
    allUsers: DeepReadonly<IIdDictionary<IUser>>,
    user?: string | any,
    duration: number | any = null,
    connectionType: string | any = null
): void {
    createLog(LogType.EVENT, Event[from], message, user, allUsers, duration, connectionType);
}

export function feedback(message: string, allUsers: DeepReadonly<IIdDictionary<IUser>>, additionalDetails: AdditionalDetails) {
    createLog(LogType.FEEDBACK, Source[Source.UI], message, undefined, allUsers, undefined, undefined, false, additionalDetails);
}

export function time(from: Source, id: string) {
    if (from === Source.API) {
        return;
    }
    const timerId = `${ from }-${ id }`;
    if (!timers.hasOwnProperty(timerId)) {
        timers[timerId] = getServerDate();
    }
}

export function timeEnd(from: Source, id: string, message: string, allUsers: DeepReadonly<IIdDictionary<IUser>>, user?: string | any, isSuccessful?: boolean): void {
    if (from === Source.API) {
        return;
    }
    const timerId = `${ from }-${ id }`;
    if (timers.hasOwnProperty(timerId)) {
        const elapsed = getServerDate().getTime() - timers[timerId].getTime();
        const messageString = typeof isSuccessful === "undefined" ? `${ message } in ${ msToDurationString(elapsed) }` : ["TIMER", message, isSuccessful ? "SUCCESS" : "FAIL", elapsed].join(" | ");
        createLog(LogType.TIMER, Source[from], messageString, user, allUsers);
        delete timers[timerId];
    }
}

export function getInstanceId() {
    return instanceId;
}

export function initiateInstanceId(saveToStorage = true, overrideRandomId?: number) {
    let instanceRandomId = overrideRandomId || Math.floor(Math.random() * 99);
    let generatedInstanceId = `${ new Date().getTime() }-${ instanceRandomId > 9 ? "" : "0" }${ instanceRandomId }`;
    if (saveToStorage && window && window.driveApp && window.driveApp.store) {
        window.driveApp.store.dispatch(DataActions.setInstanceId(generatedInstanceId));
    }
    instanceId = generatedInstanceId;
    return generatedInstanceId;
}

export function formatLogs(logsToStringify: ILog[], lineSeparator?: string, columnSeparator?: string, asArray?: boolean) {
    let stringBuilder: string[] = [];
    let logStringBuilder: string[];
    logsToStringify.forEach(logToStringify => {
        logStringBuilder = [];

        logStringBuilder.push(padString(logToStringify.type, 5));
        logStringBuilder.push(logToStringify.date);
        logStringBuilder.push(padString(logToStringify.source, 10));
        if (logToStringify.user) {
            logStringBuilder.push(`(${ logToStringify.user })`);
        }
        logStringBuilder.push(logToStringify.message);
        if (logToStringify.dateEnd && logToStringify.occurs) {
            logStringBuilder.push(`(${ logToStringify.occurs }x over ${ msToDurationString(new Date(logToStringify.dateEnd).getTime() - new Date(logToStringify.date).getTime()) })`);
        } else {
            logStringBuilder.push("");
        }
        stringBuilder.push(logStringBuilder.join(columnSeparator || "  "));
    });
    if (asArray) {
        return stringBuilder;
    }
    return stringBuilder.join(lineSeparator || "\n");
}

export async function getString(filterPred?: (log: ILog) => boolean, rowsLimit?: number) {
    let { logs } = await getAllLogsAndKeysFromDb(rowsLimit);
    if (filterPred) {
        logs = logs.filter(l => filterPred(l));
    }
    let logString = formatLogs(logs);

    return logString;
}